<template>
  <!--
    First-visit persona selector — a wall of six doors on the navy hero band.
    Shown on "/" only, only while nothing is stored in localStorage["cs.persona"].
    Esc, the close control and "Just browsing" all store the explicit skip so
    the overlay never returns on its own; picking a door stores it and routes
    to /for/<id>. Focus is trapped inside while open; motion is a single
    opening sequence that reduced-motion turns off.
  -->
  <Teleport to="body">
    <transition name="ps-fade" appear>
        <div
        class="ps-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @keydown="onKeydown"
      >
      <div ref="panel" class="ps-panel">
        <header class="ps-head">
          <div class="ps-brand" aria-hidden="true">
            <img src="/media/recraft/logo/denvstack-icon-dark.svg" alt="" class="ps-brand-icon">
            <span>DenvStack</span>
          </div>
          <button
            ref="closeBtn"
            type="button"
            class="ps-skip"
            :aria-label="$t('persona.selector.close')"
            @click="skip"
          >
            <span>{{ $t('persona.selector.skip') }}</span>
            <i class="fas fa-xmark" aria-hidden="true"/>
          </button>
        </header>

        <h1 :id="titleId" class="ps-title">{{ $t('persona.selector.title') }}</h1>
        <p class="ps-lead">{{ $t('persona.selector.lead') }}</p>

        <div class="ps-doors">
          <button
            v-for="(door, i) in doors"
            :key="door.id"
            ref="tiles"
            type="button"
            class="ps-door"
            :style="{ '--i': i }"
            @click="choose(door.id)"
          >
            <span class="ps-door-jamb" aria-hidden="true"/>
            <span class="ps-door-text">
              <span class="ps-door-label">{{ $t(door.labelKey) }}</span>
              <span class="ps-door-next">{{ $t(door.nextKey) }}</span>
            </span>
            <i class="fas fa-arrow-right ps-door-arrow" aria-hidden="true"/>
          </button>

          <button
            type="button"
            class="ps-door ps-door--ghost"
            :style="{ '--i': doors.length }"
            @click="skip"
          >
            <span class="ps-door-text">
              <span class="ps-door-label">{{ $t('persona.browsing.label') }}</span>
              <span class="ps-door-next">{{ $t('persona.browsing.next') }}</span>
            </span>
            <i class="fas fa-arrow-right ps-door-arrow" aria-hidden="true"/>
          </button>
        </div>

        <p class="ps-foot">{{ $t('persona.selector.footnote') }}</p>
      </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { DOORS, usePersona } from '@/utils/persona'

let counter = 0

export default {
  name: 'PersonaSelector',
  emits: ['done'],
  setup() {
    const { setPersona } = usePersona()
    return { setPersona }
  },
  data() {
    counter += 1
    return {
      doors: DOORS,
      titleId: `ps-title-${counter}`,
      lastBodyOverflow: '',
      previouslyFocused: null
    }
  },
  mounted() {
    this.previouslyFocused = document.activeElement
    this.lastBodyOverflow = document.body.style.overflow || ''
    document.body.style.overflow = 'hidden'
    this.$nextTick(() => {
      const first = Array.isArray(this.$refs.tiles) ? this.$refs.tiles[0] : null
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true })
    })
  },
  beforeUnmount() {
    document.body.style.overflow = this.lastBodyOverflow
    const prev = this.previouslyFocused
    if (prev && typeof prev.focus === 'function' && document.contains(prev)) prev.focus()
  },
  methods: {
    choose(id) {
      this.setPersona(id)
      this.$emit('done', id)
      this.$router.push(`/for/${id}`)
    },
    skip() {
      this.setPersona(null)
      this.$emit('done', null)
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.skip()
        return
      }
      if (event.key !== 'Tab') return
      // Focus trap: wrap at both ends of the panel's focusables.
      const root = this.$refs.panel
      if (!root) return
      const focusables = root.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }
}
</script>

<style scoped>
.ps-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000001; /* above the navbar (999999) and the org-switch loader */
  background: linear-gradient(160deg, var(--ds-primary-900) 0%, var(--ds-primary-700) 100%);
  color: var(--ds-white);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ps-panel {
  width: 100%;
  max-width: 1080px;
  min-height: 100dvh;
  padding: clamp(14px, 2.5vh, 32px) 16px clamp(14px, 2.5vh, 32px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: clamp(8px, 1.6vh, 18px);
}

.ps-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ps-brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--ds-white);
}

.ps-brand-icon {
  width: 28px;
  height: 28px;
}

.ps-skip {
  appearance: none;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: var(--ds-white);
  border-radius: 999px;
  padding: 0.4rem 0.4rem 0.4rem 0.9rem;
  font: inherit;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
}

.ps-skip i {
  width: 22px;
  height: 22px;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.8rem;
}

.ps-skip:hover { border-color: rgba(255, 255, 255, 0.6); }

.ps-title {
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: clamp(1.45rem, 1rem + 2.2vw, 2.6rem);
  line-height: 1.12;
  letter-spacing: -0.015em;
  margin: 0;
  max-width: 22ch;
}

.ps-lead {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: clamp(0.9rem, 0.82rem + 0.4vw, 1.05rem);
  line-height: 1.5;
  max-width: 56ch;
}

.ps-doors {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(8px, 1.4vh, 14px);
}

/* A door: white slab, a navy jamb on the left edge that widens to brand blue
   when the door is "opened" (hover / focus). The one memorable device. */
.ps-door {
  appearance: none;
  position: relative;
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  text-align: left;
  background: var(--ds-white);
  color: var(--ds-primary-900);
  border: 0;
  border-radius: var(--ds-radius-md);
  padding: 12px 14px 12px 0;
  min-height: 64px;
  cursor: pointer;
  font: inherit;
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.ps-door-jamb {
  align-self: stretch;
  width: 10px;
  background: var(--ds-primary-900);
  border-radius: var(--ds-radius-md) 0 0 var(--ds-radius-md);
  transition: width 0.18s ease, background-color 0.18s ease;
}

.ps-door:hover .ps-door-jamb,
.ps-door:focus-visible .ps-door-jamb {
  width: 22px;
  background: var(--ds-primary-500);
}

.ps-door:hover,
.ps-door:focus-visible {
  box-shadow: var(--ds-shadow-lg);
  transform: translateY(-1px);
}

.ps-door:focus-visible {
  outline: 3px solid var(--ds-accent);
  outline-offset: 2px;
}

.ps-door-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-door-label {
  font-family: var(--ds-font-display);
  font-weight: 700;
  font-size: clamp(0.98rem, 0.9rem + 0.35vw, 1.15rem);
  line-height: 1.25;
}

.ps-door-next {
  color: var(--ds-neutral-500);
  font-size: 0.85rem;
  line-height: 1.3;
}

.ps-door-arrow {
  color: var(--ds-primary-500);
  font-size: 0.9rem;
  margin-right: 2px;
}

/* Just browsing: a ghost door — dashed, no jamb, quieter type. */
.ps-door--ghost {
  grid-template-columns: minmax(0, 1fr) auto;
  padding-left: 16px;
  background: transparent;
  color: var(--ds-white);
  border: 1px dashed rgba(255, 255, 255, 0.45);
  box-shadow: none;
}

.ps-door--ghost .ps-door-next { color: rgba(255, 255, 255, 0.7); }
.ps-door--ghost .ps-door-arrow { color: var(--ds-white); }
.ps-door--ghost:hover,
.ps-door--ghost:focus-visible {
  border-style: solid;
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow: none;
}

.ps-foot {
  margin: 0;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
}

/* Tablet: two columns. */
@media (min-width: 541px) {
  .ps-panel { padding-inline: 24px; }
  .ps-doors { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .ps-door { min-height: 96px; padding-block: 16px; }
}

/* Desktop: three columns and taller doors — the slab reads as a door. */
@media (min-width: 1025px) {
  .ps-panel { padding-inline: 32px; min-height: 0; }
  .ps-doors { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
  .ps-door {
    grid-template-columns: 12px minmax(0, 1fr);
    grid-template-rows: 1fr auto;
    align-items: start;
    min-height: 168px;
    padding: 20px 20px 18px 0;
    gap: 0 18px;
  }
  .ps-door-jamb { grid-row: 1 / span 2; width: 12px; }
  .ps-door:hover .ps-door-jamb,
  .ps-door:focus-visible .ps-door-jamb { width: 28px; }
  .ps-door-text { grid-column: 2; }
  .ps-door-arrow { grid-column: 2; justify-self: end; margin-top: 12px; }
  .ps-door--ghost { grid-template-columns: minmax(0, 1fr); padding-left: 20px; }
  .ps-door--ghost .ps-door-text,
  .ps-door--ghost .ps-door-arrow { grid-column: 1; }
}

/* One orchestrated opening: the wall settles in once, doors a beat apart. */
.ps-fade-enter-active { transition: opacity 0.25s ease; }
.ps-fade-leave-active { transition: opacity 0.2s ease; }
.ps-fade-enter-from,
.ps-fade-leave-to { opacity: 0; }

.ps-fade-enter-active .ps-door {
  animation: ps-door-in 0.42s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(0.08s + var(--i, 0) * 0.05s);
}

@keyframes ps-door-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .ps-fade-enter-active,
  .ps-fade-leave-active { transition: none; }
  .ps-fade-enter-active .ps-door { animation: none; }
  .ps-door,
  .ps-door-jamb { transition: none; }
  .ps-door:hover,
  .ps-door:focus-visible { transform: none; }
}
</style>
